import { useMemo, useState } from 'react';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { Landmark, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { VaultData } from '../hooks/useVaultData';
import { sanitizeReferrerKey } from '../ton/referral';
import { resolveOutgoingReferrerKey } from '../ton/notify';
import { gaMoneyEvent } from '../analytics';
import {
  DEPOSIT_GAS_TON,
  MIN_DEPOSIT_GRAM,
  buildDeposit,
  buildLpBurn,
  fmtUnits9,
  parseUnits9,
} from '../ton/vault';

type Mode = 'add' | 'remove';

interface VaultLiquidityCardProps {
  data: VaultData;
}

/**
 * "Add liquidity to the mini-games" card: the live bank stats and, on tap, a
 * modal where liquidity is actually deposited into the Vault (LP PRMD minted
 * to the wallet) or withdrawn by burning LP.
 */
export function VaultLiquidityCard({ data }: VaultLiquidityCardProps) {
  const { t } = useTranslation();
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const { vault, lp, gramBalance, lpValue, refresh } = data;

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('add');
  const [amount, setAmount] = useState('');
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);

  const lpPrice = useMemo(() => {
    if (!vault || vault.totalShares <= 0n) return 1;
    return Number(vault.bankroll) / Number(vault.totalShares);
  }, [vault]);

  const sharePct = useMemo(() => {
    if (!vault || !lp || vault.totalShares <= 0n) return 0;
    return (Number(lp.balance) / Number(vault.totalShares)) * 100;
  }, [vault, lp]);

  const amountNum = Number(amount) || 0;

  const estimate = useMemo(() => {
    if (!(amountNum > 0)) return null;
    if (mode === 'add') {
      const net = Math.max(0, amountNum - DEPOSIT_GAS_TON);
      const minted = (net / lpPrice) * 0.95;
      return minted > 0 ? minted : null;
    }
    return amountNum * lpPrice;
  }, [amountNum, mode, lpPrice]);

  const maxAdd = Math.max(0, Number(gramBalance) / 1e9 - 0.1);
  const maxRemove = lp ? Number(lp.balance) / 1e9 : 0;

  const submit = async () => {
    if (!address) {
      void tonConnectUI.openModal();
      return;
    }
    if (!(amountNum > 0) || pending) return;
    setNotice(null);
    setPending(true);
    try {
      if (mode === 'add') {
        if (amountNum < MIN_DEPOSIT_GRAM + DEPOSIT_GAS_TON) {
          setNotice({
            text: t('v2.vault.errTooSmall', { min: MIN_DEPOSIT_GRAM + DEPOSIT_GAS_TON }),
            kind: 'err',
          });
          return;
        }
        const refKey = sanitizeReferrerKey(
          await resolveOutgoingReferrerKey(tonConnectUI.account?.address ?? null),
        );
        await tonConnectUI.sendTransaction(buildDeposit(amount, refKey));
        gaMoneyEvent('deposit_liquidity', 'GRAM', amountNum);
      } else {
        if (!lp?.walletAddress || lp.balance <= 0n) {
          setNotice({ text: t('v2.vault.errNoLp'), kind: 'err' });
          return;
        }
        const units = parseUnits9(amount);
        if (units > lp.balance) {
          setNotice({ text: t('v2.vault.errOverLp'), kind: 'err' });
          return;
        }
        await tonConnectUI.sendTransaction(
          buildLpBurn(lp.walletAddress, units, address),
        );
        gaMoneyEvent('withdraw_liquidity', 'GRAM', amountNum);
      }
      setNotice({ text: t('v2.vault.sent'), kind: 'ok' });
      setAmount('');
      setTimeout(refresh, 8000);
    } catch (e) {
      setNotice({
        text: t('v2.vault.errSend', {
          message: e instanceof Error ? e.message : String(e),
        }),
        kind: 'err',
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <div className="token-info-card defi-card defi-card--earn vault-card">
        <div className="defi-card__head" style={{ cursor: 'default' }}>
          <span className="defi-card__icon defi-card__icon--earn">
            <Landmark size={20} aria-hidden="true" />
          </span>
          <span className="defi-card__titles">
            <span className="defi-card__title">{t('v2.vault.title')}</span>
            <span className="defi-card__pitch">{t('v2.vault.pitch')}</span>
          </span>
        </div>
        <div className="defi-card__stats">
          <div>
            <span>{t('v2.vault.bank')}</span>
            <strong>{vault ? `${fmtUnits9(vault.bankroll)} GRAM` : '—'}</strong>
          </div>
          <div>
            <span>{t('v2.vault.lpPrice')}</span>
            <strong>{vault ? `${lpPrice.toFixed(4)} GRAM` : '—'}</strong>
          </div>
          <div>
            <span>{t('v2.vault.myPosition')}</span>
            <strong>
              {address && lp
                ? `${fmtUnits9(lpValue)} GRAM${sharePct >= 0.01 ? ` · ${sharePct.toFixed(2)}%` : ''}`
                : '—'}
            </strong>
          </div>
        </div>
        <button
          type="button"
          className="swap-submit defi-card__cta"
          onClick={() => {
            setOpen(true);
            setMode('add');
            setNotice(null);
          }}
        >
          {t('v2.vault.cta')}
        </button>
      </div>

      {open && (
        <div
          className="sand-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="sand-sheet vault-sheet">
            <div className="sand-sheet__top">
              <span className="defi-card__icon defi-card__icon--earn">
                <Landmark size={20} aria-hidden="true" />
              </span>
              <h3>{t('v2.vault.modalTitle')}</h3>
              <button
                type="button"
                className="sand-sheet__close"
                onClick={() => setOpen(false)}
                aria-label={t('v2.profile.close')}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="vault-sheet__tabs">
              <button
                type="button"
                className={mode === 'add' ? 'is-on' : ''}
                onClick={() => {
                  setMode('add');
                  setAmount('');
                  setNotice(null);
                }}
              >
                {t('v2.vault.tabAdd')}
              </button>
              <button
                type="button"
                className={mode === 'remove' ? 'is-on' : ''}
                onClick={() => {
                  setMode('remove');
                  setAmount('');
                  setNotice(null);
                }}
              >
                {t('v2.vault.tabRemove')}
              </button>
            </div>

            <div className="sand-sheet__stats">
              <div>
                <span>{t('v2.vault.bank')}</span>
                <strong>{vault ? `${fmtUnits9(vault.bankroll)} GRAM` : '—'}</strong>
              </div>
              <div>
                <span>{t('v2.vault.myLp')}</span>
                <strong>{lp ? `${fmtUnits9(lp.balance)} LP` : '0 LP'}</strong>
              </div>
              <div>
                <span>{t('v2.vault.myPosition')}</span>
                <strong>{fmtUnits9(lpValue)} GRAM</strong>
              </div>
            </div>

            <div className="sand-sheet__bal">
              <span>
                {mode === 'add'
                  ? t('v2.vault.balGram', { amount: (Number(gramBalance) / 1e9).toFixed(2) })
                  : t('v2.vault.balLp', { amount: fmtUnits9(lp?.balance ?? 0n, 4) })}
              </span>
              <button
                type="button"
                onClick={() =>
                  setAmount(
                    mode === 'add'
                      ? String(Math.floor(maxAdd * 100) / 100)
                      : String(Math.floor(maxRemove * 10000) / 10000),
                  )
                }
              >
                MAX
              </button>
            </div>
            <input
              className="sand-sheet__amt"
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder={mode === 'add' ? t('v2.vault.phGram') : t('v2.vault.phLp')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />

            {estimate != null && (
              <div className="sand-sheet__chance">
                {mode === 'add'
                  ? t('v2.vault.estLp', { amount: estimate.toFixed(4) })
                  : t('v2.vault.estGram', { amount: estimate.toFixed(2) })}
              </div>
            )}
            <p className="vault-sheet__note">
              {mode === 'add' ? t('v2.vault.noteAdd') : t('v2.vault.noteRemove')}
            </p>

            {notice && (
              <p className={`vault-sheet__notice vault-sheet__notice--${notice.kind}`}>
                {notice.text}
              </p>
            )}

            <button
              type="button"
              className="sand-sheet__go"
              disabled={pending || (!!address && !(amountNum > 0))}
              onClick={() => void submit()}
            >
              {!address
                ? t('v2.vault.connect')
                : pending
                  ? t('v2.vault.pending')
                  : mode === 'add'
                    ? t('v2.vault.submitAdd')
                    : t('v2.vault.submitRemove')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
